import deviceApi from '@/api/device';
import posApi from '@/api/pos';
import DeviceKassa from '@/models/deviceKassa';
import SettingsPosState from '@/models/settingsPosState';
import { RootState } from '@/store';
import { asyncRecursive } from '@/utils';
import Vue from 'vue';
import { ActionContext, GetterTree, MutationTree } from 'vuex';

interface DevicesState {
  devices: {
    pos: any[],
    network: any[],
  };
  isFetchingDevices: boolean;
  isDevicesWasFetch: boolean;
  posStates: { [deviceId: string]: SettingsPosState };
}

const namespaced: boolean = true;

const POS_STATE_FETCH_INTERVAL = 5000;

const posStatePollers: { [deviceId: string]: { timer: { run: Function, cancel: Function }, subscribersCount: number } } = {};

const cancelAllPosStatePollers = () => {
  Object.keys(posStatePollers).forEach((deviceId: string) => {
    posStatePollers[deviceId].timer.cancel();

    delete posStatePollers[deviceId];
  });
};

const getDefaultState = () => ({
  devices: {
    pos: [],
    network: [],
  },
  isFetchingDevices: false,
  isDevicesWasFetch: false,
  posStates: {},
});

const state: DevicesState = getDefaultState();

const getters: GetterTree<DevicesState, RootState> = {
  getPosStateByDeviceId(state) {
    return (deviceId: string): SettingsPosState | null => state.posStates[deviceId] || null;
  },
};

const actions = {
  async fetchDevices({ commit, state }: ActionContext<DevicesState, RootState>) {
    if (!state.isDevicesWasFetch && !state.isFetchingDevices) {
      commit('setIsFetchingDevices', true);

      const devices = await deviceApi.fetchDevices();

      commit('setDevices', devices);
      commit('setIsFetchingDevices', false);
      commit('setIsDevicesWasFetch', true);
    }
  },
  async editDeviceKassa({ dispatch }: ActionContext<DevicesState, RootState>, { id, editedFields }: any) {
    deviceApi.editDeviceKassa({ id, editedFields });

    return dispatch(
      'socketEditDeviceKassa',
      { id, ...editedFields },
      { root: true },
    );
  },
  async createPosDevice({ dispatch }: ActionContext<DevicesState, RootState>, data: { syncCode: string }) {
    const response = await deviceApi.createPosDevice(data);

    dispatch('socketCreateDeviceKassa', response.data, { root: true });

    return response.data;
  },
  async removePosDevice({ dispatch }: ActionContext<DevicesState, RootState>, id: string) {
    await deviceApi.removePosDevice(id);

    return dispatch('socketRemoveDeviceKassa', { id }, { root: true });
  },
  async authorizePosDevice({ dispatch }: ActionContext<DevicesState, RootState>, data: { id: string; login: string; password: string }) {
    const response = await deviceApi.authorizePosDevice(data);

    return dispatch('socketAuthorizeDeviceKassa', response.data, { root: true });
  },
  subscribeToPosState({ commit }: ActionContext<DevicesState, RootState>, deviceId: string) {
    const poller = posStatePollers[deviceId];

    if (poller !== undefined) {
      poller.subscribersCount++;

      return;
    }

    const timer = asyncRecursive(async () => {
      return posApi.getPosState(deviceId)
        .then((posState: any) => {
          commit('setPosState', { deviceId, posState: new SettingsPosState(posState) });
        })
        .catch(() => {
          commit('setPosState', { deviceId, posState: new SettingsPosState() });
        });
    }, POS_STATE_FETCH_INTERVAL);

    posStatePollers[deviceId] = { timer, subscribersCount: 1 };

    timer.run();
  },
  unsubscribeFromPosState({ commit }: ActionContext<DevicesState, RootState>, deviceId: string) {
    const poller = posStatePollers[deviceId];

    if (poller === undefined) {
      return;
    }

    poller.subscribersCount--;

    if (poller.subscribersCount < 1) {
      poller.timer.cancel();

      delete posStatePollers[deviceId];

      commit('removePosState', deviceId);
    }
  },
};

const mutations: MutationTree<DevicesState> = {
  resetState(state) {
    cancelAllPosStatePollers();

    Object.assign(state, getDefaultState());
  },
  setPosState(state, { deviceId, posState }: { deviceId: string, posState: SettingsPosState }) {
    Vue.set(state.posStates, deviceId, posState);
  },
  removePosState(state, deviceId: string) {
    Vue.delete(state.posStates, deviceId);
  },
  setDevices(state, devices: any) {
    state.devices = devices;
  },
  editDeviceKassa(state, { id: deviceId, ...editedFields }: any) {
    const editedDevice: DeviceKassa | undefined = state.devices.pos.find(({ id }: DeviceKassa) => {
      return id === deviceId;
    });

    if (editedDevice !== undefined) {
      editedDevice.mergeWithNewFields(editedFields);
    }
  },
  createDeviceKassa(state, pos: DeviceKassa) {
    state.devices.pos.push(pos);
  },
  removeDeviceKassa(state, deviceId: string) {
    const index = state.devices.pos.findIndex(({ id }: DeviceKassa) => id === deviceId);

    if (index !== -1) {
      state.devices.pos.splice(index, 1);
    }
  },
  authorizePosDevice(state, { id }: DeviceKassa) {
    const pos = state.devices.pos.find((pos: DeviceKassa) => pos.id === id);

    if (pos !== undefined) {
      pos.authorized = true;
    }
  },
  setIsFetchingDevices(state, isFetchingDevices) {
    state.isFetchingDevices = isFetchingDevices;
  },
  setIsDevicesWasFetch(state, isDevicesWasFetch) {
    state.isDevicesWasFetch = isDevicesWasFetch;
  },
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
